/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useServiceContext } from '../../../../root'
import ModalConfirmation from '../../../../components/shared/ModalConfirmation'
import HtmlCell from '../../../../components/shared/HtmlCell'
import { getWatermarkUsage, deleteWatermark } from '../../../../services/api/watermarks'
import type { Watermark } from '../../../../services/api/watermarks'
import { toast } from '../../../../services/toast'

interface ModalRemoveProps {
  watermark: Watermark
  onClose: () => void
  onSuccess?: () => void
}

const ModalRemove: React.FC<ModalRemoveProps> = ({ watermark, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const [msg, setMsg] = useState<React.ReactNode>('')
  const [loading, setLoading] = useState<Promise<any> | null>(null)

  useEffect(() => {
    selectMessage()
  }, [])

  const selectMessage = async () => {
    try {
      const promise = getWatermarkUsage(watermark.id)
      setLoading(promise)

      const data = await promise
      const usedIn = data.docTypeTplsWithWatermark || []

      const textMsg = (
        <div>
          <HtmlCell>
            {usedIn && usedIn.length > 0
              ? translate('SETTINGS_WATERMARKS_CONFIRM_MSG2') +
                ' ' +
                usedIn.length +
                ' ' +
                translate('DOCUMENT_TYPE(S)?') +
                ' <b>' +
                watermark.name +
                '</b>'
              : translate('SETTINGS_WATERMARKS_CONFIRM_MSG1') + ' <b>' + watermark.name + '</b>'}
          </HtmlCell>
        </div>
      )

      setMsg(textMsg)
      setLoading(null)
    } catch (error) {
      console.error('Error loading watermark usage:', error)
      setLoading(null)
    }
  }

  const doSave = async () => {
    try {
      await deleteWatermark(watermark.id)
      toast.success(translate('WATERMARKS_DELETE_SUCCESS_MSG'))

      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.message || 'Unknown error'
      toast.error(translate('WATERMARKS_DELETE_FAIL_MSG') + ': ' + errorMsg)
    }
  }

  return (
    <ModalConfirmation
      titleMsg={translate('WATERMARKS_REMOVE')}
      loading={loading}
      msg={msg}
      cancelTxt={translate('CANCEL')}
      okTxt={translate('OK')}
      onSubmit={doSave}
      onCancel={onClose}
    />
  )
}

export default ModalRemove
